import { collection, doc, getDocs, setDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { initFirebase } from '../lib/firebase';
import { ensureFreshToken } from '../lib/ensure-fresh-token';

/**
 * Merchant notification token document in Firestore
 * Stored in `merchantNotificationTokens` collection
 */
export interface MerchantNotificationToken {
  token: string;
  tenantId: string;
  userId: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Save or update a merchant's FCM notification token
 * Uses `{tenantId}_{userId}` as document ID (one token per user per tenant)
 */
export async function saveMerchantNotificationToken(
  tenantId: string,
  userId: string,
  token: string
): Promise<void> {
  await ensureFreshToken();
  const { db } = initFirebase();
  const docId = `${tenantId}_${userId}`;
  const tokenRef = doc(db, 'merchantNotificationTokens', docId);

  await setDoc(tokenRef, {
    token,
    tenantId,
    userId,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Remove a merchant's FCM notification token
 */
export async function removeMerchantNotificationToken(
  tenantId: string,
  userId: string
): Promise<void> {
  await ensureFreshToken();
  const { db } = initFirebase();
  const docId = `${tenantId}_${userId}`;
  const tokenRef = doc(db, 'merchantNotificationTokens', docId);

  await deleteDoc(tokenRef);
}

/**
 * Check if the current user has a notification token registered for a tenant
 */
export async function getMerchantNotificationToken(
  tenantId: string,
  userId: string
): Promise<string | null> {
  const { db } = initFirebase();
  const tokensQuery = query(
    collection(db, 'merchantNotificationTokens'),
    where('tenantId', '==', tenantId),
    where('userId', '==', userId)
  );

  const snapshot = await getDocs(tokensQuery);
  if (snapshot.empty) return null;

  return snapshot.docs[0].data().token as string;
}
